import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { springQuick, tapScale } from '../../tokens';

type ChoiceCardProps = {
  icon: ReactNode;
  title: string;
  description?: string;
  selected: boolean;
  onClick: () => void;
  /** Layout: 'tile' is square-ish for grids; 'row' is horizontal for stacks. */
  layout?: 'tile' | 'row';
};

/**
 * Selectable card used in onboarding for the cycle and budget model picks.
 * No visible borders — selection is communicated via accent-tinted bg and
 * accent-colored icon + checkmark in the corner.
 */
export function ChoiceCard({
  icon,
  title,
  description,
  selected,
  onClick,
  layout = 'tile',
}: ChoiceCardProps) {
  const base =
    'rounded-card text-left relative overflow-hidden';
  const tone = selected
    ? 'bg-accent/10 shadow-md'
    : 'bg-surface shadow-md';

  if (layout === 'row') {
    return (
      <motion.button
        type="button"
        onClick={onClick}
        whileTap={tapScale}
        transition={springQuick}
        aria-pressed={selected}
        className={cn(base, tone, 'p-card-pad flex items-center gap-3')}
      >
        <div
          className={cn(
            'w-11 h-11 rounded-button shrink-0 inline-flex items-center justify-center',
            selected
              ? 'bg-accent text-white'
              : 'bg-surface-secondary text-text-secondary',
          )}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold text-text-primary">
            {title}
          </div>
          {description ? (
            <div className="text-[13px] text-text-secondary mt-0.5 leading-snug">
              {description}
            </div>
          ) : null}
        </div>
        {selected ? (
          <div className="w-6 h-6 rounded-pill bg-accent text-white inline-flex items-center justify-center shrink-0">
            <Check size={14} strokeWidth={2.5} />
          </div>
        ) : null}
      </motion.button>
    );
  }

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={tapScale}
      transition={springQuick}
      aria-pressed={selected}
      className={cn(
        base,
        tone,
        'p-card-pad flex flex-col items-start gap-3 min-h-[132px]',
      )}
    >
      <div
        className={cn(
          'w-11 h-11 rounded-button inline-flex items-center justify-center',
          selected
            ? 'bg-accent text-white'
            : 'bg-surface-secondary text-text-secondary',
        )}
      >
        {icon}
      </div>
      <div className="flex-1 flex flex-col">
        <div className="text-[15px] font-semibold text-text-primary">
          {title}
        </div>
        {description ? (
          <div className="text-[13px] text-text-secondary mt-1 leading-snug">
            {description}
          </div>
        ) : null}
      </div>
      {selected ? (
        <div className="absolute top-3 right-3 w-6 h-6 rounded-pill bg-accent text-white inline-flex items-center justify-center">
          <Check size={14} strokeWidth={2.5} />
        </div>
      ) : null}
    </motion.button>
  );
}
